import React from 'react';
import {
  Box,
  Button,
  Divider,
  Flex,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { observer } from 'mobx-react';
import QrTypePills from './QrTypePills';
import QrMainInput from './QrMainInput';
import QrAdvancedFields from './QrAdvancedFields';
import QrOptionalFields from './QrOptionalFields';
import QrLocationSearch from './QrLocationSearch';
import QrEventFields from './QrEventFields';
import QrDesignPanel from './QrDesignPanel';
import QrPreviewPanel from './QrPreviewPanel';

const QrWorkspace = observer(({ store }) => {
  const border = useColorModeValue('gray.200', 'gray.700');
  const toast = useToast();

  const hasInput = !!store.payload;

  const handleGenerate = () => {
    if (!hasInput) return;
    store.generate();
    toast({ title: 'QR code generated!', status: 'success', duration: 1500, position: 'bottom' });
  };

  const leftBg = useColorModeValue('white', 'gray.800');
  const rightBg = useColorModeValue('gray.50', 'gray.900');

  return (
    <Box
      borderWidth="1px"
      borderColor={border}
      borderRadius="2xl"
      overflow="hidden"
      shadow={useColorModeValue('sm', 'none')}
    >
      <Flex direction={{ base: 'column', lg: 'row' }} align="stretch">
        {/* ── LEFT: Configure ── */}
        <Box
          flex={{ base: '0 0 auto', lg: '0 0 66.666%' }}
          w={{ base: '100%', lg: '66.666%' }}
          bg={leftBg}
          borderRightWidth={{ lg: '1px' }}
          borderBottomWidth={{ base: '1px', lg: '0' }}
          borderColor={border}
          overflow="hidden"
        >
          {/* Type selector */}
          <Box px={4} pt={4} pb={3} borderBottomWidth="1px" borderColor={border}>
            <QrTypePills store={store} />
          </Box>

          {/* Input area */}
          <Box px={4} pt={4} pb={3}>
            <QrMainInput store={store} />
            {store.qrTypeId === 'location'
              ? <QrLocationSearch store={store} />
              : store.qrTypeId === 'event'
              ? <QrEventFields store={store} />
              : <QrAdvancedFields store={store} />
            }
            <QrOptionalFields store={store} />
          </Box>

          {/* Generate button */}
          <Box px={4} pb={4}>
            <Button
              w="full"
              colorScheme="blue"
              isDisabled={!hasInput}
              onClick={handleGenerate}
            >
              Generate QR Code
            </Button>
          </Box>

          <Divider borderColor={border} />

          {/* Design */}
          <Box px={4} py={4}>
            <QrDesignPanel store={store} />
          </Box>
        </Box>

        {/* ── RIGHT: Output ── */}
        <Box
          flex={{ base: '0 0 auto', lg: '1' }}
          w={{ base: '100%', lg: 'auto' }}
          minW={0}
          bg={rightBg}
        >
          <QrPreviewPanel store={store} />
        </Box>
      </Flex>
    </Box>
  );
});

export default QrWorkspace;
